/** @jsxImportSource @emotion/react */

import {
  Header,
  useSession,
  formatErrorResponse,
} from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  createStyles,
  Icon,
  useTheme,
  Modal,
  ListItem,
  Copy,
  useForm,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { cloneDeep } from 'lodash';
import { useMemo, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncFn, useUpdateEffect } from 'react-use';
import { BackofficeUsers } from 'components';
import { FCUCards } from 'components/FCUCards';
import { getMerchant } from 'tabs/api';
import {
  ContactInformation,
  KybSave,
  KybFormData,
  RelatedPerson,
} from 'tabs/Merchants/types';
import { Merchant } from 'tabs/types';
import { Permissions } from '../../../../constants';
import { addMerchant, getKYB, saveKYB, updateMerchant } from '../../api';
import {
  kybValidators,
  merchantValidators,
  autofillMerchant,
  autofillKyb,
} from '../../utils';
import { MerchantForm, KYBForm, KybChangelog } from './components';
import { Branding } from './components/MerchantForm/components/Branding';
import { DEFAULT_ONRAMP_CURRENCY_CODE } from './components/MerchantForm/MerchantForm';

interface Props {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const defaultMerchantForm: Merchant = {
  name: '',
  displayName: '',
  status: 'ACTIVE',
  countryCode: 'SE',

  payFee: {
    type: 'PERCENTAGE',
    value: 0,
  },
  onrampFee: {
    type: 'PERCENTAGE',
    value: 0,
  },
  processingFee: {
    type: 'PERCENTAGE',
    value: 0,
  },
  gatewayFee: {
    type: 'FIXED',
    value: 0.3,
  },
  settlementFee: {
    type: 'FIXED',
    value: 20.0,
  },
  corporateWalletReceiveExternalFee: {
    type: 'PERCENTAGE',
    value: 0,
  },
  corporateWalletReceiveExternalFeeMinimumApplicableAmount: 100,
  corporateWalletSendInternalFee: {
    type: 'PERCENTAGE',
    value: 0,
  },
  corporateWalletSendExternalFee: {
    type: 'PERCENTAGE',
    value: 0,
  },
  customerFee: null,
  config: {
    hideQbBrand: false,
    hideFiatAmount: false,
    hideCustomerFee: false,
    amountToleranceUpperBound: 1,
    amountToleranceLowerBound: 1,
    allowPayOrders: true,
    allowOnrampOrders: false,
    allowProcessingOrders: false,
    canAccessCreditCardInfo: false,
    canAccessPersonalInformation: false,
    minutesToCancelOrderAfter: 0,
    cancelOrderOnError: false,
    retryOrderCallbacks: true,
    allowCorporateWallet: false,
    useWalletRecipientDuality: false,
    allowTrustedTraffic: false,
    allowBackofficeOrderCreation: false,
    onrampCurrency: DEFAULT_ONRAMP_CURRENCY_CODE,
    layoutMode: 'ORDERS',
  },
  paymentDetails: {
    iban: '',
    bic: '',
    reference: 'Quickbit',
  },
};

const defaultContactInfo: ContactInformation = {
  address1: '',
  country: '',
  postalNumber: '',
  postalRegion: '',
};

const defaultKybForm: KybFormData = {
  contactEmail: null,
  fullCompanyName: '',
  previousCompanyNames: null,
  incorporationDate: null,
  onboardingDate: null,
  registrationNumber: '',
  countryOfTaxResidence: null,
  taxIdentificationNumber: null,
  leiNumber: null,
  incorporationCountry: undefined,
  businessSectors: [],
  expectedMonthlyVolume: 0,
  riskScore: null,
  riskScoreAdjustment: 0,
  usaConnection: false,
  kybApproved: false,
  registeredAddress: defaultContactInfo,
  primaryAddress: { ...defaultContactInfo },
  relatedPeople: [],
  businessOperations: '',
  reasonForUse: '',
  authorizedSignatory: '',
  licenseForBusiness: null,
  jurisdictions: '',
  turnover: 0,
  expectedMonthlyTransactions: 0,
  settlementReportEmails: null,
};

export const MerchantDetails = ({ onSuccess, onError }: Props) => {
  const navigate = useNavigate();
  const { merchantId } = useParams();
  const isNewMerchant = merchantId === 'new';
  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const session = useSession();
  const [apiKey, setApiKey] = useState('');
  const {
    fields: merchantForm,
    setters: merchantFormSetters,
    errors: merchantFormErrors,
    edited: merchantFormEdited,
    hasErrors: merchantFormHasErrors,
  } = useForm(defaultMerchantForm, merchantValidators, {
    hasDependentValidators: true,
  });

  const {
    fields: kybForm,
    setters: kybFormSetters,
    errors: kybFormErrors,
    edited: kybFormEdited,
    hasErrors: kybFormHasErrors,
  } = useForm<KybFormData>(defaultKybForm, kybValidators, {
    hasDependentValidators: true,
  });

  const [{ value: kyb, error: getKYBError }, fetchKYB] =
    useAsyncFn(async () => {
      if (merchantId && merchantId !== 'new') {
        const { changelog, ...kybData } = await getKYB(merchantId);
        kybFormSetters.set({
          ...kybData,
          settlementReportEmails:
            kybData.settlementReportEmails?.join(',') || null,
          primaryAddress: kybData.primaryAddress ?? { ...defaultContactInfo },
          businessSectors: kybData.businessSectors,
          relatedPeople: kybData.relatedPeople.map((p) => ({
            ...p,
            address: p.address ?? { ...defaultContactInfo },
          })),
        });
        return {
          kybData,
          changelog,
        };
      }
      return undefined;
    }, [merchantId]);

  const [{ error: getMerchantError }, fetchMerchant] = useAsyncFn(async () => {
    if (merchantId && merchantId !== 'new') {
      const m = await getMerchant(merchantId);
      merchantFormSetters.set(cloneDeep(m));
      void fetchKYB();
      return m;
    }
    return null;
  }, [merchantId, fetchKYB]);

  useEffect(() => {
    void fetchMerchant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);
  const [{ error: updateKybError, loading: updateKybLoading }, updateKyb] =
    useAsyncFn(
      async (merchantId: string) => {
        const newKyb: KybSave = {
          ...kybForm,
          id: merchantId,
          relatedPeople: kybForm.relatedPeople.map((p) => {
            if (!p.userReference?.startsWith('new')) return p as RelatedPerson;

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const newP: RelatedPerson = { ...p, dateOfBirth: p.dateOfBirth! };
            delete newP['userReference'];
            return newP;
          }),
          settlementReportEmails:
            kybForm.settlementReportEmails?.split(',').map((s) => s.trim()) ||
            null,
        } as KybSave;

        const response = await saveKYB(merchantId, newKyb);
        void fetchKYB();
        return response;
      },
      [kybForm]
    );
  const [{ error: addMerchantError, loading: addMerchantLoading }, add] =
    useAsyncFn(async () => {
      const newKyb: KybSave = {
        ...kybForm,
        id: merchantId,
        relatedPeople: kybForm.relatedPeople.map((p) => {
          if (!p.userReference?.startsWith('new')) return p as RelatedPerson;

          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const newP: RelatedPerson = { ...p, dateOfBirth: p.dateOfBirth! };
          delete newP['userReference'];
          return newP;
        }),
        settlementReportEmails:
          kybForm.settlementReportEmails?.split(',').map((s) => s.trim()) ||
          null,
      } as KybSave;
      const newMerchant = await addMerchant(
        merchantForm,
        newKyb,
        kybForm.contactEmail ?? ''
      );
      void fetchKYB();
      setApiKey(newMerchant.apiKey);
      onSuccess('Merchant added successfully');
      return newMerchant;
    }, [merchantForm, fetchKYB, kybForm]);

  const autofill = () => {
    merchantFormSetters.set(autofillMerchant(defaultMerchantForm));
    kybFormSetters.set(autofillKyb(defaultKybForm));
  };
  const [
    { error: updateMerchantError, loading: updateMerchantLoading },
    update,
  ] = useAsyncFn(async () => {
    const updated = await updateMerchant(merchantForm);
    onSuccess('Merchant updated successfully');
    return updated;
  }, [merchantForm]);

  useUpdateEffect(() => {
    if (getMerchantError) {
      onError(formatErrorResponse(getMerchantError));
    }
    if (getKYBError) {
      onError(formatErrorResponse(getKYBError));
    }
    if (addMerchantError) {
      onError(formatErrorResponse(addMerchantError));
    }
    if (updateMerchantError) {
      onError(formatErrorResponse(updateMerchantError));
    }
    if (updateKybError) {
      onError(formatErrorResponse(updateKybError));
    }
  }, [
    getMerchantError,
    getKYBError,
    addMerchantError,
    updateMerchantError,
    updateKybError,
  ]);

  const editable = useMemo(() => {
    if (
      session.authenticated &&
      !session.permissions.includes('edit_merchants')
    )
      return false;
    if (merchantForm.deletedAt) return false;
    return true;
  }, [session, merchantForm.deletedAt]);

  const canManageCorporateWallet = useMemo(() => {
    if (
      session.authenticated &&
      !session.permissions.includes(Permissions.VIEW_CORPORATE_WALLET)
    )
      return false;
    return true;
  }, [session]);

  const disableSaving =
    kybFormHasErrors ||
    merchantFormHasErrors ||
    (!kybFormEdited && !merchantFormEdited);

  return (
    <>
      <Header>
        <div css={styles.headerContent}>
          <div css={styles.titleContainer}>
            <div css={styles.back} onClick={() => navigate('/merchants')}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                Merchants
              </Typography>
            </div>
            <div css={styles.chevron}>
              <Icon name="chevron" size={spacing.xxs} />
            </div>
            <Typography variant="subtitle">
              {isNewMerchant ? 'New merchant' : merchantForm.name}
            </Typography>
          </div>
          {canManageCorporateWallet && merchantForm.uuid && (
            <UtilityButton
              text="Corporate Wallet"
              onClick={() =>
                navigate(`/merchants/${merchantId}/corporate-wallet`)
              }
            />
          )}
          {isNewMerchant && (
            <>
              <UtilityButton
                text="Cancel"
                variant="secondary"
                onClick={() => navigate('/merchants')}
              />
              <UtilityButton
                text="Save merchant"
                variant="primary"
                loading={addMerchantLoading}
                disabled={!editable || disableSaving || addMerchantLoading}
                onClick={add}
              />
              {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
                <UtilityButton
                  text="autofill"
                  variant="tertiary"
                  onClick={autofill}
                />
              )}
            </>
          )}
        </div>
      </Header>
      <div css={styles.wrappingContainer}>
        <div
          css={{ gap: spacing.xxs, display: 'flex', flexDirection: 'column' }}
        >
          <MerchantForm
            formErrors={merchantFormErrors}
            formFields={merchantForm}
            formSetters={merchantFormSetters}
            hasError={merchantFormHasErrors}
            hasBeenEdited={merchantFormEdited}
            onSuccess={onSuccess}
            editable={editable}
            loading={updateMerchantLoading}
            onSave={update}
          />
          {!isNewMerchant && (
            <div>
              <BackofficeUsers
                merchantId={merchantId as string}
                onSuccess={onSuccess}
                onError={onError}
              />
            </div>
          )}

          {!isNewMerchant && (
            <Branding
              onSuccess={onSuccess}
              onError={onError}
              merchantId={merchantId as string}
            />
          )}
        </div>
        <div
          css={{
            gap: spacing.xxs,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            css={{
              gap: spacing.xxs,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <KYBForm
              formFields={kybForm}
              formErrors={kybFormErrors}
              formSetters={kybFormSetters}
              editable={editable}
              hasError={kybFormHasErrors}
              hasBeenEdited={kybFormEdited}
              loading={updateKybLoading}
              onSave={async () => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                await updateKyb(merchantForm.uuid!);
                onSuccess('KYB updated successfully');
              }}
            />
            {kyb?.changelog && kyb.changelog.length > 0 && (
              <KybChangelog changes={kyb.changelog} />
            )}
          </div>
          {!isNewMerchant && (
            <div>
              <FCUCards
                customerId={merchantId as string}
                customerType="MERCHANT"
              />
            </div>
          )}
        </div>
      </div>

      {apiKey && (
        <Modal
          title="API Key"
          subtitle="Please copy this API Key and send it to the merchant."
          primaryButtonProps={{
            text: 'I have copied the API Key',
            onClick: () => {
              setApiKey('');
              void navigate(`/merchants`);
            },
          }}
        >
          <div css={styles.apiKey}>
            <ListItem
              title="API Key"
              subtitle={apiKey}
              action={<Copy textToCopy={apiKey} />}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  wrappingContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: spacing.xs,
  },
  headerContent: {
    display: 'grid',
    gap: spacing.xxs,
    gridTemplateColumns: '1fr auto auto',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  back: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
      textDecorationColor: palette.grey.x300,
    },
  },
  chevron: {
    transform: 'rotate(180deg)',
    padding: `0 ${spacing.xxs}`,
    svg: {
      fill: 'none',
      stroke: palette.grey.x300,
    },
  },
  apiKey: {
    li: {
      border: `1px solid ${palette.grey.x200}`,
      borderRadius: shape.borderRadius,
      color: palette.black,
    },
  },
}));
